import styled from 'styled-components';
import { NumberFormatValues, SourceInfo } from 'react-number-format';
import { useQuoteBuilderActions } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/form-context';
import { useQuoteBuilderView } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/view';
import { useDrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import { useQuoteBuilderModalState } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/modal-context';
import { useDebouncedTrackAction } from '@paradigm/features/src/analytics';
import Button from '@paradigm/design-system/src/components/Button';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import TwoWayInput from '#/components/UnifiedRfqs/drfq/quote-builder/TwoWayInput';
import { AnonIconText } from '#/components/UnifiedRfqs/components/WarningMessage';

function PriceField() {
  const { price, isAnonymousRfq } = useQuoteBuilderView();
  const { changePrice, clearTwoWayPrice } = useQuoteBuilderActions();
  const { legPricingEnabled } = useQuoteBuilderModalState();
  const drfq = useDrfqRfq();
  const [bidPrice, askPrice] = price.value;
  const trackAction = useDebouncedTrackAction();

  const handleBidPriceChange = (
    bid: NumberFormatValues,
    sourceInfo: SourceInfo,
  ) => {
    if (sourceInfo.source === 'event')
      trackAction('change strategy price', { side: 'BUY' });
    changePrice([bid.formattedValue, askPrice]);
  };

  const handleAskPriceChange = (
    ask: NumberFormatValues,
    sourceInfo: SourceInfo,
  ) => {
    if (sourceInfo.source === 'event')
      trackAction('change strategy price', { side: 'SELL' });
    changePrice([bidPrice, ask.formattedValue]);
  };

  return (
    <TwoWayContainer>
      {drfq.role === 'TAKER' && (
        <HiddenLimitHeader>Hidden Limit (Beta)</HiddenLimitHeader>
      )}
      <TitleContainer>
        <Title>Two-Way</Title>
        {isAnonymousRfq && <AnonIconText />}
      </TitleContainer>
      <TwoWayInput
        rightValue={askPrice}
        leftValue={bidPrice}
        decimalScale={price.precision}
        isLeftDisabled={legPricingEnabled}
        isRightDisabled={legPricingEnabled}
        onLeftValueChange={handleBidPriceChange}
        onRightValueChange={handleAskPriceChange}
      />
      {!legPricingEnabled && (
        <Button
          css="align-self: end;margin-top: -4px;"
          ariaLabel="Clear"
          variant="link"
          size="extra-small"
          onClick={clearTwoWayPrice}
        >
          Clear
        </Button>
      )}
    </TwoWayContainer>
  );
}

export default PriceField;

const HiddenLimitHeader = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  color: white;
`;

const TitleContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 0.25rem;
`;

const Title = styled.p`
  margin: 0px;
  height: 16px;
  width: 58px;
  color: #ffffff;
  font-family: 'IBM Plex Sans';
  font-size: 0.75rem;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 0.875rem;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1rem;
    font-weight: normal;
    width: auto;
    height: auto;
    line-height: 1.5rem;
  }
`;

export const TwoWayContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px 0px;
`;
